import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tooltip,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Upload,
  Modal,
  message,
} from 'antd';
import {
  CloseOutlined,
  EyeOutlined,
  FileTextOutlined,
  GlobalOutlined,
  FacebookOutlined,
  InstagramOutlined,
  PlusCircleFilled,
  PlusOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import EmployeeOptionItem from '@app/components/common/EmployeeOption/EmployeeOptionItem';
import { IoMdTime } from 'react-icons/io';
import { FaMapMarker } from 'react-icons/fa';
import { FaNoteSticky } from 'react-icons/fa6';
import { MdAttachFile } from 'react-icons/md';
import 'components/order/Order.styles.css';
import { getOrderChannels } from '@app/api/orderChannel.api';
import { OrderChannelModel } from '@app/interfaces/order/channel';
import useDebounce from '@app/hooks/useDebounce';
import AddressAutocomplete from '@app/components/common/AddressAutocomplete';

/**
 * Ô địa chỉ giao: chọn từ gợi ý Google thì điền luôn định danh địa điểm + toạ độ vào các
 * field ẩn; gõ tay thì xoá chúng đi để backend tự tra lại (toạ độ cũ không còn đúng địa chỉ).
 */
const DeliveryAddressField: React.FC<{ value?: string; onChange?: (value: string) => void }> = ({
  value,
  onChange,
}) => {
  const form = Form.useFormInstance();

  return (
    <AddressAutocomplete
      value={value}
      onChange={onChange}
      placeholder="Nhập địa chỉ giao hàng chi tiết..."
      rows={3}
      onSelect={(place) =>
        form.setFieldsValue({
          delivery_place_id: place.place_id,
          delivery_lat: place.lat,
          delivery_lng: place.lng,
          delivery_formatted_address: place.formatted_address,
        })
      }
      onManualInput={() =>
        form.setFieldsValue({
          delivery_place_id: undefined,
          delivery_lat: undefined,
          delivery_lng: undefined,
          delivery_formatted_address: undefined,
        })
      }
    />
  );
};
import { createCustomer } from '@app/store/slices/customerSlice';
import { searchCustomersForOrderApi } from '@app/api/customer.api';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { RootState } from '@app/store/store';
import CustomerForm from '@app/components/partner/customer/CustomerForm';
import { TbFriends } from 'react-icons/tb';
import { EmployeeOption } from '@app/interfaces/employee';
import OverviewModal from '@app/components/partner/customer/overview/OverviewModal';
import CustomerPointsSummary from '@app/components/partner/customer/components/CustomerPointsSummary';
import CustomerPointsAlert from '@app/components/partner/customer/components/CustomerPointsAlert';
import { StoreOption } from '@app/interfaces/store';
import { SiZalo, SiTiktok } from 'react-icons/si';
import { FaDiagnoses } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { formatMoney } from '@app/utils/utils';
import { getFullBlobUrl } from '@app/utils/blobUrl';
import { noAvatar } from 'constants/noAvatar';
import { DATE_TIME_FORMAT } from '@app/constants/dateFormats';
import { applyPoints, pointValueOf } from './orderPoints';

interface OrderCustomerProps {
  form: FormInstance;
  setCustomer: (customer: any) => void;
  sellerOptions: EmployeeOption[];
  storeOptions: StoreOption[];
  setTotalAmount: (amount: number) => void;
  totalOriginalOrder?: number;
  returnOrder?: boolean;
  onRegisterCalcFn?: (fn: () => void) => void;
  shipperOptions?: EmployeeOption[];
  designerOptions?: EmployeeOption[];
  fileList?: any[];
  uploadProps?: any;
  isNewOrder?: boolean;
  /** Khách chọn sẵn khi mở form (đơn tạm tạo từ hộp thư). */
  initialCustomer?: Record<string, any> | null;
  /** Đơn tạm đã có địa chỉ giao → không tự điền địa chỉ của khách chọn sẵn. */
  keepInitialAddress?: boolean;
  /** Đổi / bỏ khách → lưu vào đơn tạm để F5 khôi phục đúng thẻ khách. */
  onCustomerChange?: (customer: Record<string, any> | null) => void;
}

const employeeOptionItem = (item: any) => <EmployeeOptionItem item={item} />;

const getChannelIcon = (channelName: string) => {
  const name = channelName.toLowerCase();
  if (name.includes('facebook')) return <FacebookOutlined style={{ color: '#1877f2', fontSize: '16px' }} />;
  if (name.includes('instagram')) return <InstagramOutlined style={{ color: '#e4405f', fontSize: '16px' }} />;
  if (name.includes('zalo')) return <SiZalo style={{ color: '#0068ff', fontSize: '16px' }} />;
  if (name.includes('website') || name.includes('web'))
    return <GlobalOutlined className="tw-text-success-teal" style={{ fontSize: '16px' }} />;
  if (name.includes('trực tiếp') || name.includes('direct'))
    return <ShopOutlined className="tw-text-warning" style={{ fontSize: '16px' }} />;
  if (name.includes('google')) return <FcGoogle style={{ fontSize: '16px' }} />;
  if (name.includes('tiktok')) return <SiTiktok style={{ color: '#010101', fontSize: '16px' }} />;
  if (name.includes('khách anh thông')) return <TbFriends style={{ color: '#1877f2', fontSize: '16px' }} />;
  return <FaDiagnoses className="tw-text-on-surface-variant" style={{ fontSize: '16px' }} />;
};

const OrderCustomerForm: React.FC<OrderCustomerProps> = ({
  form,
  setCustomer,
  sellerOptions,
  storeOptions,
  setTotalAmount,
  returnOrder,
  onRegisterCalcFn,
  shipperOptions = [],
  designerOptions = [],
  fileList = [],
  uploadProps,
  isNewOrder,
  initialCustomer,
  keepInitialAddress,
  onCustomerChange,
}) => {
  // NV bán chính không được chọn lại làm bán cùng (backend cũng loại, đây chỉ để UI khỏi rối).
  const primarySellerId = Form.useWatch('employee_id', form);
  const pointsAvailable = Number(Form.useWatch('points_available', form)) || 0;
  const pointsRequested = Number(Form.useWatch('points_requested', form)) || 0;
  const pointValueWatch = Number(Form.useWatch('point_value', form)) || 0;
  const [date, setDate] = useState(moment());
  const [channels, setChannels] = useState<OrderChannelModel[]>([]);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [showCustomerOverviewModal, setShowCustomerOverviewModal] = useState(false);
  const [searchTermCustomer, setSearchTermCustomer] = useState<string>('');
  const debouncedSearchTermCustomer = useDebounce(searchTermCustomer, 500);

  const [customers, setCustomers] = useState<any[]>([]);
  const { productItems } = useAppSelector((state: RootState) => state.order || {});

  const dispatch = useAppDispatch();

  // ─── Calculation logic ─────────────────────────────────────────────────────

  const orderSummary = useMemo(
    () =>
      productItems.reduce(
        (acc, product) => {
          acc.totalQty += product.qty;
          acc.beforeDiscountAmount += Number(product.amount);
          return acc;
        },
        { totalQty: 0, beforeDiscountAmount: 0 },
      ),
    [productItems],
  );

  const calTotalAmount = useCallback(() => {
    const shippingFee = Number(form.getFieldValue('shipping_fee')) || 0;
    const discountPercent = Number(form.getFieldValue('discount_percent')) || 0;
    const vatPercent = Number(form.getFieldValue('vat_percent')) || 0;
    const otherAmount = Number(form.getFieldValue('other_amount')) || 0;
    const base = orderSummary.beforeDiscountAmount;

    const discount = discountPercent > 0 ? (discountPercent / 100) * base : 0;
    const beforeVat = base - discount + shippingFee;
    const vatAmount = vatPercent > 0 ? (vatPercent / 100) * beforeVat : 0;
    const gross = beforeVat + vatAmount + otherAmount;
    // Điểm khách đồng ý dùng (points_requested) có thể nhiều hơn bill hiện tại — mỗi lần tính lại chỉ áp
    // đủ số cần, thêm hàng sau thì điểm được áp tiếp. Sửa đơn cũ: lấy số điểm đã lưu.
    const pointValue =
      Number(form.getFieldValue('point_value')) ||
      pointValueOf(form.getFieldsValue(['points_used', 'points_discount_amount']));
    const requested = form.getFieldValue('points_requested') ?? (Number(form.getFieldValue('points_used')) || 0);
    const points = returnOrder
      ? { pointsUsed: 0, discount: 0 }
      : applyPoints({ points: Number(requested) || 0, pointValue, gross });
    const amount = gross - points.discount;

    setTotalAmount(amount);

    form.setFieldsValue({
      points_requested: requested,
      point_value: pointValue,
      points_used: points.pointsUsed,
      points_discount_amount: points.discount,
      before_discount_amount: returnOrder ? -base : base,
      discount_amount: discount,
      vat_amount: vatAmount,
      total_amount: returnOrder ? -amount : amount,
      debit_amount: returnOrder ? amount : -amount,
    });
  }, [form, orderSummary, returnOrder, setTotalAmount]);

  // Re-run when product totals change
  useEffect(() => {
    calTotalAmount();
  }, [orderSummary.beforeDiscountAmount, orderSummary.totalQty]);

  // Keep the registered ref fresh so OrderProductItems always calls latest version
  const calTotalAmountLatest = useRef(calTotalAmount);
  useEffect(() => {
    calTotalAmountLatest.current = calTotalAmount;
  });
  useEffect(() => {
    onRegisterCalcFn?.(() => calTotalAmountLatest.current());
  }, []);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    fetchChannels();
  }, []);

  useEffect(() => {
    if (debouncedSearchTermCustomer !== undefined) {
      fetchCustomers();
    }
  }, [debouncedSearchTermCustomer]);

  const fetchChannels = async () => {
    try {
      const response = await getOrderChannels();
      setChannels(response as OrderChannelModel[]);
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const fetchCustomers = async () => {
    const results = await searchCustomersForOrderApi(debouncedSearchTermCustomer ?? '');
    setCustomers(results);
  };

  // ─── Event handlers ─────────────────────────────────────────────────────────

  const onDateChange = (value: any) => setDate(value);
  const handleAddCustomer = () => setShowCustomerForm(true);
  const onChangeChannel = (value: string) => form.setFieldsValue({ order_channel_id: value });
  const onChangeStore = (value: string) => form.setFieldsValue({ store_id: value });
  const onSearchCustomer = useCallback((value: string) => setSearchTermCustomer(value), []);

  const filterCustomerOption = useCallback(
    (input: string, option: any) => (option?.label || '').toLowerCase().includes(input.toLowerCase()),
    [],
  );

  const formatCustomer = useCallback((item: any) => `${item.customer_code}-${item.name}`, []);

  const customerOptionItem = useCallback(
    (item: any) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Avatar
          src={getFullBlobUrl(item.avatar_url) || undefined}
          icon={<UserOutlined />}
          size={28}
          className="tw-bg-primary tw-shrink-0"
        />
        <div>
          <div className="tw-font-medium tw-text-on-surface">{item.name}</div>
          <div className="tw-text-[11px] tw-text-on-surface-variant">
            {item.customer_code}
            {item.phone && ` • ${item.phone}`}
          </div>
        </div>
      </div>
    ),
    [],
  );

  useEffect(() => {
    if (!initialCustomer?.id) return;
    setSelectedCustomer(initialCustomer);
    setCustomer({
      id: initialCustomer.id,
      name: initialCustomer.name,
      address: keepInitialAddress ? undefined : initialCustomer.full_address,
    });
    form.setFieldsValue({
      customer_id: initialCustomer.id,
      points_available: Number(initialCustomer.points_usable ?? initialCustomer.number_of_points) || 0,
      point_value: Number(initialCustomer.point_value) || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCustomer?.id]);

  // Điểm thuộc về khách: đổi / bỏ khách thì bỏ điểm đang áp, khách mới có điểm thì hỏi áp dụng luôn không.
  const resetPoints = (pointValue = 0, available = 0) => {
    form.setFieldsValue({ points_requested: 0, point_value: pointValue, points_available: available });
    calTotalAmount();
  };

  const reservedPoints = Number(selectedCustomer?.points_reserved) || 0;

  const applyCustomerPoints = () => {
    form.setFieldsValue({ points_requested: pointsAvailable });
    calTotalAmount();
  };

  const askApplyPoints = (customer: any) => {
    const points = Number(customer?.points_usable ?? customer?.number_of_points) || 0;
    const pointValue = Number(customer?.point_value) || 0;
    resetPoints(pointValue, points);
    if (returnOrder || points <= 0 || pointValue <= 0) return;

    Modal.confirm({
      title: 'Xác nhận!',
      content: `Khách ${customer.name} đang có ${points} điểm tích luỹ (= ${formatMoney(
        points * pointValue,
      )}), hạn dùng đến hết năm nay. Áp dụng trừ vào bill luôn không?`,
      okText: 'Đồng ý',
      cancelText: 'Huỷ',
      onOk: () => {
        form.setFieldsValue({ points_requested: points, point_value: pointValue });
        calTotalAmount();
      },
    });
  };

  const onChangeCustomer = (value: string) => {
    form.setFieldsValue({ customer_id: value });
    const found = customers.find((item) => item.id === Number(value));
    setCustomer({ id: found?.id, name: found?.name, address: found?.full_address });
    setSelectedCustomer(found || null);
    onCustomerChange?.(found || null);
    askApplyPoints(found);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    form.setFieldsValue({ customer_id: undefined });
    setCustomer({});
    onCustomerChange?.(null);
    resetPoints();
  };

  const onNewCustomer = async (values: any) => {
    try {
      const response = await dispatch(createCustomer(values));
      if (response.payload !== undefined) {
        setShowCustomerForm(false);
        // Refresh customers list to show newly created customer with avatar
        await fetchCustomers();
        message.success('Khách hàng mới được tạo thành công!');
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  // ─── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {/* ═══ Card 1: Nguồn đơn ═══════════════════════════════════════════════ */}
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div className="order-section-title" style={{ marginBottom: 12 }}>
          <ShopOutlined className="tw-mr-1.5 tw-text-primary" />
          Nguồn đơn
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div>
            <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant">
              <ShopOutlined className="tw-mr-1 tw-text-primary" />Cửa hàng
            </div>
            <Form.Item name="store_id" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                placeholder="Chọn cửa hàng"
                optionFilterProp="children"
                onChange={onChangeStore}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={storeOptions}
              />
            </Form.Item>
          </div>
          <div>
            <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant">
              <FileTextOutlined className="tw-mr-1 tw-text-primary" />Kênh bán
            </div>
            <Form.Item name="order_channel_id" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                placeholder="Chọn kênh bán"
                onChange={onChangeChannel}
                optionLabelProp="label"
                filterOption={(input, option: any) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
              >
                {channels.map((channel) => (
                  <Select.Option
                    key={channel.id}
                    value={channel.id}
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {getChannelIcon(channel.name)}{channel.name}
                      </span>
                    }
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {getChannelIcon(channel.name)}
                      <span className="tw-text-[13px] tw-font-medium tw-text-on-surface">{channel.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant">
          <span className="text-danger tw-mr-0.5">*</span>Thời gian tạo đơn
        </div>
        <Form.Item
          name="trans_date"
          rules={[{ required: true, message: 'Vui lòng chọn thời gian' }]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker
            showTime
            format={DATE_TIME_FORMAT}
            onChange={onDateChange}
            value={date}
            style={{ width: '100%' }}
            placeholder="Chọn ngày giờ"
          />
        </Form.Item>
      </Card>

      {/* ═══ Card 2: Khách hàng + Giao hàng + Ghi chú ══════════════════════ */}
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div className="order-section-title" style={{ marginBottom: 12 }}>
          <UserOutlined className="tw-mr-1.5 tw-text-primary" />
          <span className="text-danger tw-mr-0.5">*</span>Khách hàng
        </div>
        {!selectedCustomer ? (
          <>
            <Form.Item
              name="customer_id"
              rules={[{ required: true, message: 'Vui lòng chọn khách hàng' }]}
              style={{ marginBottom: 6 }}
            >
              <Select
                showSearch
                placeholder="Tìm tên, SĐT, mã KH..."
                onChange={onChangeCustomer}
                onSearch={onSearchCustomer}
                filterOption={filterCustomerOption}
                optionLabelProp="label"
              >
                {customers.map((customer) => (
                  <Select.Option key={customer.id} value={customer.id} label={formatCustomer(customer)}>
                    {customerOptionItem(customer)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                type="link"
                size="small"
                icon={<PlusCircleFilled />}
                onClick={handleAddCustomer}
                style={{ padding: 0, fontSize: 12 }}
              >
                Thêm khách hàng mới
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-solid tw-border-outline-variant tw-bg-surface-container-low tw-px-2.5 tw-py-2">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar 
                  src={getFullBlobUrl(selectedCustomer.avatar_url) || noAvatar}
                  icon={<UserOutlined />}
                  size={28}
                  className="tw-bg-primary tw-shrink-0"
                />
                <div>
                  <div className="tw-text-sm tw-font-semibold tw-text-on-surface">{selectedCustomer.name}</div>
                  {selectedCustomer.phone && (
                    <div className="tw-text-[11px] tw-text-on-surface-variant">{selectedCustomer.phone}</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <EyeOutlined
                  className="tw-cursor-pointer tw-text-sm tw-text-primary"
                  onClick={() => setShowCustomerOverviewModal(true)}
                />
                <CloseOutlined
                  className="tw-cursor-pointer tw-text-xs tw-text-on-surface-variant"
                  onClick={handleClearCustomer}
                />
              </div>
            </div>
            {/* Lỡ bấm Huỷ ở hộp thoại hỏi điểm thì vẫn thấy cảnh báo và áp dụng lại được. */}
            {!returnOrder && pointsRequested === 0 && (
              <CustomerPointsAlert
                className="tw-mb-2"
                points={pointsAvailable + reservedPoints}
                reserved={reservedPoints}
                pointValue={pointValueWatch}
                onApply={applyCustomerPoints}
              />
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <div className="tw-flex-1 tw-rounded-md tw-border tw-border-solid tw-border-surface-container-low tw-bg-surface-subtle tw-px-2.5 tw-py-2 tw-text-center">
                <div className="tw-mb-0.5 tw-text-[11px] tw-text-on-surface-variant">Tổng chi tiêu</div>
                <div className="tw-text-[13px] tw-font-bold tw-text-primary">
                  {formatMoney(selectedCustomer.total_order_amount ?? 0)}
                </div>
              </div>
              <div className="tw-flex-1 tw-rounded-md tw-border tw-border-solid tw-border-surface-container-low tw-bg-surface-subtle tw-px-2.5 tw-py-2 tw-text-center">
                <div className="tw-mb-0.5 tw-text-[11px] tw-text-on-surface-variant">Số đơn đã mua</div>
                <div className="tw-text-[13px] tw-font-bold tw-text-primary">{selectedCustomer.number_of_orders ?? 0} đơn</div>
              </div>
            </div>
            <CustomerPointsSummary
              compact
              className="tw-mt-2"
              summary={selectedCustomer.points_summary}
              pointValue={selectedCustomer.point_value}
            />
            <Form.Item name="customer_id" hidden>
              <input type="hidden" />
            </Form.Item>
          </>
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* Thông tin giao hàng */}
        <div className="tw-mb-2.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.6px] tw-text-on-surface-variant" style={{ marginBottom: 8 }}>
          <IoMdTime className="tw-mr-1 tw-text-[13px] tw-text-primary" />
          Thông tin giao hàng
        </div>
        <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant" style={{ marginBottom: 4 }}>
          <span className="text-danger tw-mr-0.5">*</span>Thời gian giao hàng
        </div>
        <Form.Item
          name="delivery_date"
          rules={[{ required: true, message: 'Vui lòng chọn thời gian giao hàng!' }]}
          style={{ marginBottom: 8 }}
        >
          <DatePicker
            showTime
            format={DATE_TIME_FORMAT}
            style={{ width: '100%' }}
            placeholder="Chọn ngày và giờ giao hàng..."
          />
        </Form.Item>
        <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant" style={{ marginBottom: 4 }}>
          <FaMapMarker className="tw-mr-1 tw-text-primary" />Địa chỉ giao hàng
        </div>
        <Form.Item name="delivery_address" style={{ marginBottom: 0 }}>
          <DeliveryAddressField />
        </Form.Item>
        {/* Chọn từ gợi ý thì kèm định danh địa điểm + toạ độ — backend tính km theo đúng điểm đó. */}
        <Form.Item name="delivery_place_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="delivery_lat" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="delivery_lng" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="delivery_formatted_address" hidden>
          <Input />
        </Form.Item>

        <Divider style={{ margin: '12px 0' }} />

        {/* Ghi chú */}
        <div className="tw-mb-2.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.6px] text-primary" style={{ marginBottom: 8 }}>
          <FaNoteSticky style={{ marginRight: 4 }} />Ghi chú đơn hàng
        </div>
        <Form.Item name="remark" style={{ marginBottom: 0 }}>
          <Input.TextArea placeholder="Thêm ghi chú cho đơn hàng (nếu có)..." rows={4} style={{ borderRadius: 8 }} />
        </Form.Item>
      </Card>

      {/* ═══ Card 3: Nhân viên ═══════════════════════════════════════════════ */}
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div className="order-section-title" style={{ marginBottom: 12 }}>
          <TeamOutlined className="tw-mr-1.5 tw-text-primary" />
          Nhân viên
        </div>

        <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant" style={{ marginBottom: 4 }}>
          <UserOutlined className="tw-mr-1 tw-text-primary" /><span className="text-danger tw-mr-0.5">*</span>Nhân viên bán hàng
        </div>
        <Form.Item
          name="employee_id"
          rules={[{ required: true, message: 'Vui lòng chọn nhân viên' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            showSearch
            placeholder="Chọn nhân viên bán hàng"
            filterOption={(input, option: any) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
            optionLabelProp="label"
          >
            {sellerOptions.map((employee: any) => (
              <Select.Option key={employee.value} value={employee.value} label={employee.label}>
                {employeeOptionItem(employee)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div className="tw-mb-[3px] tw-mt-3 tw-text-[11px] tw-font-medium tw-text-on-surface-variant" style={{ marginBottom: 4 }}>
          <TeamOutlined className="tw-mr-1 tw-text-primary" />Nhân viên bán cùng
          <Tooltip title="Thưởng bán hàng của đơn chia đều cho NV bán hàng + các NV bán cùng khi đơn hoàn tất.">
            <InfoCircleOutlined className="tw-ml-1 tw-text-outline" />
          </Tooltip>
        </div>
        <Form.Item name="co_seller_ids" style={{ marginBottom: 0 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Chọn thêm nhân viên bán cùng (nếu có)..."
            filterOption={(input, option: any) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
            optionLabelProp="label"
            maxTagCount="responsive"
          >
            {sellerOptions
              .filter((employee: any) => employee.value !== primarySellerId)
              .map((employee: any) => (
                <Select.Option key={employee.value} value={employee.value} label={employee.label}>
                  {employeeOptionItem(employee)}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Divider style={{ margin: '12px 0' }} />

        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant" style={{ marginBottom: 4 }}>
              <UserOutlined className="tw-mr-1 tw-text-primary" />Nhân viên thiết kế
            </div>
            <Form.Item
              name="designer_ids"
              style={{ marginBottom: 0 }}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Chọn nhân viên thiết kế..."
                filterOption={(input, option: any) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
                optionLabelProp="label"
                maxTagCount="responsive"
              >
                {designerOptions.map((e: any) => (
                  <Select.Option key={e.value} value={e.value} label={e.label}>
                    {employeeOptionItem(e)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <div className="tw-mb-[3px] tw-text-[11px] tw-font-medium tw-text-on-surface-variant" style={{ marginBottom: 4 }}>
              <UserOutlined className="tw-mr-1 tw-text-primary" />Nhân viên giao hàng
            </div>
            <Form.Item
              name="shipper_id"
              //rules={[{ required: true, message: 'Vui lòng chọn nhân viên giao hàng!' }]}
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                placeholder="Chọn nhân viên giao hàng..."
                filterOption={(input, option: any) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
                optionLabelProp="label"
              >
                {shipperOptions.map((e: any) => (
                  <Select.Option key={e.value} value={e.value} label={e.label}>
                    {employeeOptionItem(e)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* ═══ Card 4: Đính kèm hình ảnh (new orders only) ════════════════════ */}
      {isNewOrder && uploadProps && (
        <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div className="order-section-title" style={{ marginBottom: 12 }}>
            <MdAttachFile style={{ marginRight: 6 }} />Đính kèm hình ảnh xác nhận
          </div>
          <Upload.Dragger
            {...uploadProps}
            style={{ background: '#fafafa', border: '2px dashed #d9d9d9', borderRadius: 8, padding: 12 }}
            showUploadList={fileList.length > 0}
          >
            <div
              className="tw-text-on-surface-variant"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontSize: 13,
              }}
            >
              <PlusOutlined style={{ fontSize: 16 }} />
              <span>Tải lên</span>
            </div>
          </Upload.Dragger>
        </Card>
      )}

      {showCustomerForm && (
        <CustomerForm
          open={showCustomerForm}
          onCreate={onNewCustomer}
          onCancel={() => setShowCustomerForm(false)}
          title="Tạo mới khách hàng"
          record={null}
        />
      )}

      {showCustomerOverviewModal && (
        <OverviewModal
          open={showCustomerOverviewModal}
          record={selectedCustomer}
          onClose={() => setShowCustomerOverviewModal(false)}
          onApplyPoints={returnOrder ? undefined : applyCustomerPoints}
          pointsApplied={pointsRequested > 0}
        />
      )}
    </div>
  );
};

export default OrderCustomerForm;
